import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';
import { AtSign, Check, X, Sparkles, Globe, Calendar, AlertTriangle } from 'lucide-react';
import { getAgeFromDob, getAgeGroup } from '../utils/ageUtils';
import './UsernameSetupModal.css';

export default function UsernameSetupModal() {
  const { completeUsernameSetup, dbUser } = useAuth();
  const { t, lang, setLang } = useTranslation();

  // ── Step 1: Username state ────────────────────────────────────────────────
  const [username, setUsername]   = useState('');
  const [status, setStatus]       = useState(null);   // 'checking'|'available'|'taken'|'invalid'
  const [msg, setMsg]             = useState('');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  // ── Step 2: Date of birth state ───────────────────────────────────────────
  const [step, setStep]           = useState(1);      // 1 = username, 2 = dob
  // Pre-fill today so iOS WebView picker opens with a committed value instead of auto-committing on focus
  const [dob, setDob]             = useState(() => new Date().toISOString().split('T')[0]);
  const [dobError, setDobError]   = useState('');
  const [blocked, setBlocked]     = useState(false);  // true when age < 13

  const timerRef = useRef();
  const inputRef = useRef();

  // ── Suggest username from display name ────────────────────────────────────
  useEffect(() => {
    if (dbUser?.name) {
      const suggested = dbUser.name
        .toLowerCase()
        .replace(/[^a-z0-9_.]/g, '')
        .slice(0, 20);
      setUsername(suggested);
      if (suggested) checkUsername(suggested);
    }
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []); // eslint-disable-line

  // ── Username availability check ───────────────────────────────────────────
  function checkUsername(val) {
    clearTimeout(timerRef.current);
    if (!val.trim()) { setStatus(null); setMsg(''); return; }
    if (!/^[a-zA-Z0-9_.]{3,30}$/.test(val)) {
      setStatus('invalid');
      setMsg(t('username.invalid'));
      return;
    }
    setStatus('checking');
    timerRef.current = setTimeout(async () => {
      try {
        const r = await api.get(`/api/profile/check-username?username=${encodeURIComponent(val)}`);
        setStatus(r.available ? 'available' : 'taken');
        setMsg(r.available ? t('username.available') : t('username.taken'));
      } catch { setStatus(null); }
    }, 350);
  }

  function handleChange(e) {
    const val = e.target.value.replace(/\s/g, '');
    setUsername(val);
    setError('');
    checkUsername(val);
  }

  // ── Step 1 submit: advance to step 2 ─────────────────────────────────────
  function handleStep1Submit(e) {
    e.preventDefault();
    if (status !== 'available') return;
    setStep(2);
  }

  // ── Step 2: DOB change — no validation here; iOS auto-fill triggers onChange
  //    immediately on focus, so we defer all validation to the submit handler.
  function handleDobChange(e) {
    setDob(e.target.value);
    setDobError('');
  }

  // ── Step 2 submit: validate age then save username + DOB together ─────────
  async function handleStep2Submit(e) {
    e.preventDefault();
    setDobError('');
    if (!dob) { setDobError(t('username.dobRequired')); return; }
    const age = getAgeFromDob(dob);
    if (age === null || age < 13) {
      setBlocked(true);
      return;
    }
    setSaving(true); setError('');
    try {
      await api.put('/api/profile', {
        username:    username.toLowerCase(),
        dateOfBirth: new Date(dob).toISOString(),
      });
      completeUsernameSetup();
    } catch (err) {
      setError(err.message || t('username.errorSave'));
      setStep(1); // return to step 1 on error so user sees the message
    } finally {
      setSaving(false);
    }
  }

  const canSubmitStep1 = status === 'available' && username.length >= 3 && !saving;
  const canSubmitStep2 = !!dob && !saving;
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="username-modal-overlay">
      <div className="username-modal">

        {/* Language toggle — always visible */}
        <div className="username-lang-hint">
          <Globe size={13} strokeWidth={2}/>
          <button
            className={lang === 'es' ? 'active' : ''}
            onClick={() => setLang(lang === 'es' ? 'en' : 'es')}>
            {lang === 'es' ? 'Switch to English' : t('username.preferSpanish')}
          </button>
        </div>

        {/* Step indicator */}
        <p className="username-step-indicator">{t('username.stepOf', step, 2)}</p>

        {/* ── STEP 1: Username ─────────────────────────────────────────── */}
        {step === 1 && (
          <>
            <div className="username-modal-icon">
              <Sparkles size={28} strokeWidth={1.5} color="var(--orange)" />
            </div>
            <h2 className="username-modal-title">{t('username.title')}</h2>
            <p className="username-modal-desc">{t('username.desc')}</p>

            {dbUser?.name && (
              <p className="username-modal-welcome">
                {t('username.welcome', dbUser.name)}
              </p>
            )}

            {error && <div className="username-modal-error">{error}</div>}

            <form onSubmit={handleStep1Submit} className="username-modal-form">
              <div className={`username-input-wrap ${status || ''}`}>
                <AtSign size={16} className="username-at-icon" strokeWidth={2} />
                <input
                  ref={inputRef}
                  type="text"
                  className="username-input"
                  value={username}
                  onChange={handleChange}
                  placeholder={t('username.placeholder')}
                  maxLength={30}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                />
                {status === 'checking' && (
                  <span className="username-status-icon">
                    <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                  </span>
                )}
                {status === 'available' && (
                  <span className="username-status-icon available"><Check size={14} strokeWidth={2.5} /></span>
                )}
                {(status === 'taken' || status === 'invalid') && (
                  <span className="username-status-icon taken"><X size={14} strokeWidth={2.5} /></span>
                )}
              </div>

              {msg && (
                <p className={`username-msg ${status}`}>{msg}</p>
              )}

              <button
                type="submit"
                className="btn btn-primary username-submit"
                disabled={!canSubmitStep1}
              >
                {t('username.next')}
              </button>
            </form>

            <p className="username-modal-hint">{t('username.hint')}</p>
          </>
        )}

        {/* ── STEP 2: Date of Birth (not blocked) ──────────────────────── */}
        {step === 2 && !blocked && (
          <>
            <div className="username-modal-icon">
              <Calendar size={28} strokeWidth={1.5} color="var(--orange)" />
            </div>
            <h2 className="username-modal-title">{t('username.dobTitle')}</h2>
            <p className="username-modal-desc">{t('username.dobDesc')}</p>

            {dobError && <div className="username-modal-error">{dobError}</div>}

            <form onSubmit={handleStep2Submit} className="username-modal-form">
              <div className="username-dob-wrap">
                <label className="username-dob-label">{t('username.dobLabel')}</label>
                <input
                  type="date"
                  className="username-dob-input"
                  value={dob}
                  onChange={handleDobChange}
                  max={today}
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary username-submit"
                disabled={!canSubmitStep2}
              >
                {saving
                  ? <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                  : t('username.finishSetup')}
              </button>
            </form>

            <button
              className="username-back-btn"
              onClick={() => { setStep(1); setDobError(''); }}
            >
              {t('common.back2')}
            </button>
          </>
        )}

        {/* ── STEP 2: Under-13 block ────────────────────────────────────── */}
        {step === 2 && blocked && (
          <>
            <div className="username-modal-icon">
              <AlertTriangle size={28} strokeWidth={1.5} color="var(--orange)" />
            </div>
            <h2 className="username-modal-title">{t('username.blockedTitle')}</h2>
            <p className="username-modal-desc">{t('username.blockedMsg')}</p>

            <button
              className="username-back-btn"
              onClick={() => { setBlocked(false); setDob(''); setDobError(''); }}
            >
              {t('username.enterDifferentDob')}
            </button>
          </>
        )}

      </div>
    </div>
  );
}
